import { useCallback, useEffect, useState } from 'react'
import {
  ArrowRight,
  Calendar,
  CheckCircle2,
  Copy,
  Loader2,
  Mail,
  Shield,
  Sparkles,
  Users,
} from 'lucide-react'
import ReactMarkdown from 'react-markdown'
import { api } from '../../api/client'
import { useAuth, type OnboardingPhase } from '../../auth/AuthProvider'

/**
 * s60 — Onboarding em 5 fases.
 *
 * Component state machine que reflecte `onboardingPhase` (vem via /auth/me):
 *
 *   null          → Phase0GoogleGate   (user acabou de entrar, ainda não ligou Google)
 *   phase1_*      → Phase1Ingesting    ("A construir memória")
 *   phase2_*      → Phase2Linking      ("A criar ligações neuronais")
 *   phase3_*      → Phase3LlmImport    ("Trazer contexto doutro LLM")
 *   phase4_*      → Phase4Summary      ("Visão global")
 *   phase5_* / complete / abandoned → redirect para /chat (ProtectedRoute)
 *
 * Poll a /api/onboarding/status cada 3s durante phase1 e phase2 para
 * detectar transições sem full page reload. Nas fases 3/4 a transição
 * acontece por acção do user (submit).
 */

type OnboardingStatus = {
  phase: OnboardingPhase
  brain_ready_at: string | null
  backfills: Record<string, BackfillState | null>
  llm_import_prompt: string
  progress_hint: {
    backfills_completed: number
    backfills_failed: number
    backfills_total_tracked: number
  }
}

type BackfillState = {
  status: 'pending' | 'running' | 'completed' | 'failed'
  events_seen: number
  events_ingested: number
  created_at: string | null
  completed_at: string | null
  error_message: string | null
}

export function OnboardingPage() {
  const { onboardingPhase, refreshOnboardingStatus } = useAuth()
  const [status, setStatus] = useState<OnboardingStatus | null>(null)
  const [polling, setPolling] = useState(true)

  const fetchStatus = useCallback(async () => {
    try {
      const s = await api.get<OnboardingStatus>('/onboarding/status')
      setStatus(s)
      // Refresh onboarding phase em AuthProvider sempre que o server
      // reporta nova fase — isto faz com que o componente re-renderize
      // na fase certa quando backend transita.
      if (s.phase !== onboardingPhase) {
        await refreshOnboardingStatus()
      }
    } catch {
      // Ignore transient errors; próximo poll vai apanhar
    }
  }, [onboardingPhase, refreshOnboardingStatus])

  // Poll a cada 3s durante fases que transitam automaticamente (1 e 2).
  // Nas fases 3/4, poll só quando chegam ao ecrã (não esperando transição
  // automática — a transição é via acção do user).
  useEffect(() => {
    if (!polling) return
    fetchStatus()
    const shouldPoll =
      onboardingPhase === 'phase1_ingestion' ||
      onboardingPhase === 'phase2_linking' ||
      // Quando em phase3 e user fez /import, background task está a gerar
      // summary — queremos detectar transição para phase4.
      onboardingPhase === 'phase3_llm_import'
    if (!shouldPoll) return
    const t = setInterval(fetchStatus, 3000)
    return () => clearInterval(t)
  }, [onboardingPhase, polling, fetchStatus])

  // Routing
  if (onboardingPhase === null) {
    return <Phase0GoogleGate />
  }
  if (onboardingPhase === 'phase1_ingestion') {
    return <PhaseShell
      title="A construir memória"
      tagline="Sem memória, o cérebro não funciona."
      progress={backfillProgress(status)}
    />
  }
  if (onboardingPhase === 'phase2_linking') {
    return <PhaseShell
      title="A criar ligações neuronais"
      tagline="Sem ligações, não existe cérebro — só memória isolada."
      progress={50}  // Fase 2 é opaca do lado do user; barra a ~meio
      spinner
    />
  }
  if (onboardingPhase === 'phase3_llm_import') {
    return <Phase3LlmImport
      prompt={status?.llm_import_prompt ?? ''}
      onAfterSubmit={() => setPolling(true)}
    />
  }
  if (onboardingPhase === 'phase4_summary') {
    return <Phase4Summary />
  }

  // phase5_chat / complete / abandoned — fallback enquanto ProtectedRoute
  // redirige.
  return (
    <PhaseShell
      title="Pronto"
      tagline="A passar-te para o chat..."
      progress={100}
    />
  )
}

// ── Fase 0 — Google OAuth gate (pre-pipeline) ──────────────────────────────

function Phase0GoogleGate() {
  const { signOut } = useAuth()
  const [connecting, setConnecting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function connectGoogle() {
    setConnecting(true)
    setError(null)
    try {
      const { auth_url } = await api.post<{ auth_url: string }>(
        '/connectors/gmail/connect',
      )
      window.location.href = auth_url
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Não consegui iniciar a ligação. Tenta de novo.')
      setConnecting(false)
    }
  }

  return (
    <div
      className="flex items-center justify-center min-h-screen px-4 py-10"
      style={{ background: 'var(--cream)', fontFamily: 'var(--sans)' }}
    >
      <div className="w-full max-w-md">
        <div className="text-xs font-mono mb-6 tracking-widest uppercase text-center" style={{ color: 'var(--ink-muted)' }}>
          se<span style={{ color: 'var(--accent)', fontStyle: 'italic' }}>Guru</span>
        </div>
        <h1
          className="text-2xl font-semibold text-center mb-3"
          style={{ color: 'var(--ink)', fontFamily: 'var(--serif)', lineHeight: 1.25 }}
        >
          Para começar, liga a tua conta Google
        </h1>
        <p
          className="text-sm text-center mb-8 leading-relaxed"
          style={{ color: 'var(--ink-light)' }}
        >
          Sem acesso ao teu email e calendário, eu sou basicamente um
          Claude com UI bonita. É daqui que aprendo quem és, com quem
          trabalhas e no que estás a trabalhar.
        </p>
        <div
          className="rounded-lg border p-5 mb-4 space-y-3"
          style={{ borderColor: 'var(--border)', background: '#fff' }}
        >
          <p className="text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--ink-muted)' }}>
            O que leio
          </p>
          <div className="flex items-start gap-3">
            <Mail size={15} strokeWidth={1.5} style={{ color: 'var(--ink-muted)' }} className="mt-0.5 shrink-0" />
            <div>
              <p className="text-sm" style={{ color: 'var(--ink)' }}>Gmail</p>
              <p className="text-xs" style={{ color: 'var(--ink-muted)' }}>
                3 meses de emails operacionais, recebidos e enviados.
              </p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Calendar size={15} strokeWidth={1.5} style={{ color: 'var(--ink-muted)' }} className="mt-0.5 shrink-0" />
            <div>
              <p className="text-sm" style={{ color: 'var(--ink)' }}>Calendário</p>
              <p className="text-xs" style={{ color: 'var(--ink-muted)' }}>
                3 meses para trás + o que aí vem.
              </p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Users size={15} strokeWidth={1.5} style={{ color: 'var(--ink-muted)' }} className="mt-0.5 shrink-0" />
            <div>
              <p className="text-sm" style={{ color: 'var(--ink)' }}>Contactos</p>
              <p className="text-xs" style={{ color: 'var(--ink-muted)' }}>
                Para te lembrar quem é quem.
              </p>
            </div>
          </div>
        </div>
        <div
          className="rounded-lg border p-5 mb-6 space-y-3"
          style={{ borderColor: 'var(--border)', background: 'rgba(45,90,61,0.04)' }}
        >
          <p className="text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--ink-muted)' }}>
            O que nunca faço
          </p>
          <div className="flex items-start gap-3">
            <Shield size={15} strokeWidth={1.5} style={{ color: 'var(--accent)' }} className="mt-0.5 shrink-0" />
            <div>
              <p className="text-sm" style={{ color: 'var(--ink)' }}>Enviar emails em teu nome</p>
              <p className="text-xs" style={{ color: 'var(--ink-muted)' }}>
                Posso propor drafts. Revês e envias tu — sempre.
              </p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Shield size={15} strokeWidth={1.5} style={{ color: 'var(--accent)' }} className="mt-0.5 shrink-0" />
            <div>
              <p className="text-sm" style={{ color: 'var(--ink)' }}>Partilhar os teus dados</p>
              <p className="text-xs" style={{ color: 'var(--ink-muted)' }}>
                Tudo cifrado, só tu vês.
              </p>
            </div>
          </div>
        </div>

        {error && (
          <p className="text-xs text-center mb-3" style={{ color: '#dc2626' }}>
            {error}
          </p>
        )}

        <button
          onClick={connectGoogle}
          disabled={connecting}
          className="w-full py-3 rounded-lg text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-40 transition-colors"
          style={{ background: 'var(--ink)', color: 'var(--cream)' }}
          onMouseEnter={e => { if (!e.currentTarget.disabled) e.currentTarget.style.background = 'var(--accent)' }}
          onMouseLeave={e => (e.currentTarget.style.background = 'var(--ink)')}
        >
          {connecting ? (
            <><Loader2 size={14} className="animate-spin" />A iniciar ligação...</>
          ) : (
            <>Ligar Google<ArrowRight size={14} /></>
          )}
        </button>

        <button
          onClick={signOut}
          className="w-full mt-4 py-2 text-xs transition-colors"
          style={{ color: 'var(--ink-muted)' }}
          onMouseEnter={e => (e.currentTarget.style.color = 'var(--ink-light)')}
          onMouseLeave={e => (e.currentTarget.style.color = 'var(--ink-muted)')}
        >
          Agora não — sair
        </button>
      </div>
    </div>
  )
}

// ── PhaseShell — reusável entre Fase 1 e Fase 2 (ecrã bloqueante + barra) ──

function PhaseShell({
  title,
  tagline,
  progress,
  spinner = false,
}: {
  title: string
  tagline: string
  progress: number  // 0-100
  spinner?: boolean
}) {
  return (
    <div
      className="flex items-center justify-center min-h-screen px-4"
      style={{ background: 'var(--cream)', fontFamily: 'var(--sans)' }}
    >
      <div className="w-full max-w-sm text-center">
        <div className="text-xs font-mono mb-8 tracking-widest uppercase" style={{ color: 'var(--ink-muted)' }}>
          se<span style={{ color: 'var(--accent)', fontStyle: 'italic' }}>Guru</span>
        </div>
        <h1
          className="text-2xl font-semibold mb-3"
          style={{ color: 'var(--ink)', fontFamily: 'var(--serif)' }}
        >
          {title}
        </h1>
        <p className="text-sm leading-relaxed mb-10" style={{ color: 'var(--ink-light)' }}>
          {tagline}
        </p>
        <div
          className="h-1.5 w-full rounded-full overflow-hidden"
          style={{ background: 'rgba(26,24,20,0.08)' }}
        >
          <div
            className="h-full transition-all duration-700 ease-out"
            style={{
              width: `${Math.max(5, Math.min(100, progress))}%`,
              background: 'var(--accent)',
            }}
          />
        </div>
        {spinner && (
          <Loader2
            size={14}
            className="animate-spin mt-6 mx-auto"
            style={{ color: 'var(--ink-muted)' }}
          />
        )}
      </div>
    </div>
  )
}

// ── Fase 3 — Importar doutro LLM ───────────────────────────────────────────

function Phase3LlmImport({
  prompt,
  onAfterSubmit,
}: {
  prompt: string
  onAfterSubmit: () => void
}) {
  const { refreshOnboardingStatus } = useAuth()
  const [mode, setMode] = useState<'choice' | 'importing'>('choice')
  const [content, setContent] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [copied, setCopied] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function submitImport() {
    if (!content.trim()) {
      setError('Cola o markdown que copiaste do outro LLM.')
      return
    }
    setSubmitting(true)
    setError(null)
    try {
      await api.post('/onboarding/phase3/import', { content: content.trim() })
      // O background task vai gerar Fase 4; o AuthProvider vai detectar a
      // transição via poll. Fica em ecrã de progresso até lá.
      await refreshOnboardingStatus()
      onAfterSubmit()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Falhou ao ingerir. Tenta de novo.')
      setSubmitting(false)
    }
  }

  async function skipImport() {
    setSubmitting(true)
    setError(null)
    try {
      await api.post('/onboarding/phase3/skip')
      await refreshOnboardingStatus()
      onAfterSubmit()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Falhou a avançar.')
      setSubmitting(false)
    }
  }

  async function copyPrompt() {
    try {
      await navigator.clipboard.writeText(prompt)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      // fallback — nada. o textarea está visível, o user pode seleccionar.
    }
  }

  if (mode === 'choice') {
    return (
      <div
        className="flex items-center justify-center min-h-screen px-4 py-10"
        style={{ background: 'var(--cream)', fontFamily: 'var(--sans)' }}
      >
        <div className="w-full max-w-md">
          <div className="text-xs font-mono mb-8 tracking-widest uppercase text-center" style={{ color: 'var(--ink-muted)' }}>
            se<span style={{ color: 'var(--accent)', fontStyle: 'italic' }}>Guru</span>
          </div>
          <h1
            className="text-2xl font-semibold text-center mb-3"
            style={{ color: 'var(--ink)', fontFamily: 'var(--serif)' }}
          >
            Já usas outro LLM?
          </h1>
          <p className="text-sm text-center mb-8 leading-relaxed" style={{ color: 'var(--ink-light)' }}>
            Se tens histórico com ChatGPT, Claude ou Gemini, podes trazer o
            que eles sabem sobre ti. Aceleras a curva.
          </p>
          <button
            onClick={() => setMode('importing')}
            className="w-full py-3 rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-colors mb-3"
            style={{ background: 'var(--ink)', color: 'var(--cream)' }}
            onMouseEnter={e => { e.currentTarget.style.background = 'var(--accent)' }}
            onMouseLeave={e => { e.currentTarget.style.background = 'var(--ink)' }}
          >
            <Sparkles size={14} />Trazer contexto
          </button>
          <button
            onClick={skipImport}
            disabled={submitting}
            className="w-full py-3 rounded-lg text-sm font-medium border transition-colors disabled:opacity-40"
            style={{ borderColor: 'var(--border)', color: 'var(--ink-light)', background: 'transparent' }}
          >
            {submitting ? 'A avançar...' : 'Avançar sem isso'}
          </button>
          {error && (
            <p className="text-xs text-center mt-3" style={{ color: '#dc2626' }}>
              {error}
            </p>
          )}
        </div>
      </div>
    )
  }

  return (
    <div
      className="flex items-start justify-center min-h-screen px-4 py-10"
      style={{ background: 'var(--cream)', fontFamily: 'var(--sans)' }}
    >
      <div className="w-full max-w-2xl">
        <div className="text-xs font-mono mb-6 tracking-widest uppercase text-center" style={{ color: 'var(--ink-muted)' }}>
          se<span style={{ color: 'var(--accent)', fontStyle: 'italic' }}>Guru</span>
        </div>
        <h1
          className="text-xl font-semibold mb-3"
          style={{ color: 'var(--ink)', fontFamily: 'var(--serif)' }}
        >
          Passo 1 — Copia este prompt
        </h1>
        <p className="text-sm mb-4" style={{ color: 'var(--ink-light)' }}>
          Cola numa nova conversa com o ChatGPT / Claude / Gemini. Ele vai
          produzir um markdown estruturado com tudo o que sabe sobre ti.
        </p>

        <div
          className="rounded-lg border relative mb-6"
          style={{ borderColor: 'var(--border)', background: '#fff' }}
        >
          <button
            onClick={copyPrompt}
            className="absolute top-2 right-2 text-xs px-3 py-1.5 rounded flex items-center gap-1.5 transition-colors z-10"
            style={{
              background: copied ? 'var(--accent)' : 'var(--ink)',
              color: 'var(--cream)',
            }}
          >
            {copied ? <><CheckCircle2 size={12} />Copiado</> : <><Copy size={12} />Copiar</>}
          </button>
          <pre
            className="p-4 pt-10 text-xs whitespace-pre-wrap leading-relaxed overflow-x-auto max-h-64"
            style={{ color: 'var(--ink)', fontFamily: 'var(--sans)' }}
          >
            {prompt}
          </pre>
        </div>

        <h2
          className="text-xl font-semibold mb-3"
          style={{ color: 'var(--ink)', fontFamily: 'var(--serif)' }}
        >
          Passo 2 — Cola o resultado aqui
        </h2>
        <p className="text-sm mb-3" style={{ color: 'var(--ink-light)' }}>
          O markdown que o outro LLM gerou.
        </p>

        <textarea
          value={content}
          onChange={e => setContent(e.target.value)}
          placeholder="# Perfil&#10;..."
          rows={10}
          className="w-full p-4 rounded-lg border text-sm font-mono mb-4"
          style={{
            borderColor: 'var(--border)',
            background: '#fff',
            color: 'var(--ink)',
            resize: 'vertical',
          }}
        />

        {error && (
          <p className="text-xs mb-3" style={{ color: '#dc2626' }}>
            {error}
          </p>
        )}

        <div className="flex gap-3">
          <button
            onClick={() => setMode('choice')}
            disabled={submitting}
            className="flex-1 py-3 rounded-lg text-sm font-medium border transition-colors disabled:opacity-40"
            style={{ borderColor: 'var(--border)', color: 'var(--ink-light)', background: 'transparent' }}
          >
            Voltar
          </button>
          <button
            onClick={submitImport}
            disabled={submitting || !content.trim()}
            className="flex-[2] py-3 rounded-lg text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-40 transition-colors"
            style={{ background: 'var(--ink)', color: 'var(--cream)' }}
            onMouseEnter={e => { if (!e.currentTarget.disabled) e.currentTarget.style.background = 'var(--accent)' }}
            onMouseLeave={e => { e.currentTarget.style.background = 'var(--ink)' }}
          >
            {submitting ? (
              <><Loader2 size={14} className="animate-spin" />A ingerir...</>
            ) : (
              <>Ingerir e avançar<ArrowRight size={14} /></>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Fase 4 — Síntese qualitativa ───────────────────────────────────────────

function Phase4Summary() {
  const { refreshOnboardingStatus } = useAuth()
  const [summary, setSummary] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [acknowledging, setAcknowledging] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    (async () => {
      try {
        const file = await api.get<{ content: string }>(
          '/vault/files/Meta/onboarding-sumario.md',
        )
        setSummary(file.content)
      } catch {
        setSummary(null)
      } finally {
        setLoading(false)
      }
    })()
  }, [])

  async function acknowledge() {
    setAcknowledging(true)
    setError(null)
    try {
      await api.post('/onboarding/phase4/acknowledge')
      await refreshOnboardingStatus()
      // ProtectedRoute vai redirigir para /chat automaticamente quando
      // phase transitar para phase5_chat
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Falhou ao avançar.')
      setAcknowledging(false)
    }
  }

  return (
    <div
      className="flex items-start justify-center min-h-screen px-4 py-10"
      style={{ background: 'var(--cream)', fontFamily: 'var(--sans)' }}
    >
      <div className="w-full max-w-2xl">
        <div className="text-xs font-mono mb-6 tracking-widest uppercase text-center" style={{ color: 'var(--ink-muted)' }}>
          se<span style={{ color: 'var(--accent)', fontStyle: 'italic' }}>Guru</span>
        </div>
        <h1
          className="text-2xl font-semibold mb-2"
          style={{ color: 'var(--ink)', fontFamily: 'var(--serif)' }}
        >
          Isto é o que percebi de ti
        </h1>
        <p className="text-sm mb-8" style={{ color: 'var(--ink-light)' }}>
          Afinamos daqui.
        </p>

        <div
          className="rounded-lg border p-6 mb-6"
          style={{ borderColor: 'var(--border)', background: '#fff' }}
        >
          {loading ? (
            <div className="flex items-center gap-2 text-sm" style={{ color: 'var(--ink-muted)' }}>
              <Loader2 size={14} className="animate-spin" />A carregar...
            </div>
          ) : summary ? (
            <article
              className="text-sm leading-relaxed prose prose-sm max-w-none"
              style={{ color: 'var(--ink)' }}
            >
              <ReactMarkdown>{summary}</ReactMarkdown>
            </article>
          ) : (
            <p className="text-sm italic" style={{ color: 'var(--ink-muted)' }}>
              Não foi possível carregar a síntese. Podes avançar na mesma — a
              rotina Onboarding vai afinar a partir daqui.
            </p>
          )}
        </div>

        {error && (
          <p className="text-xs mb-3" style={{ color: '#dc2626' }}>
            {error}
          </p>
        )}

        <button
          onClick={acknowledge}
          disabled={acknowledging}
          className="w-full py-3 rounded-lg text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-40 transition-colors"
          style={{ background: 'var(--ink)', color: 'var(--cream)' }}
          onMouseEnter={e => { if (!e.currentTarget.disabled) e.currentTarget.style.background = 'var(--accent)' }}
          onMouseLeave={e => { e.currentTarget.style.background = 'var(--ink)' }}
        >
          {acknowledging ? (
            <><Loader2 size={14} className="animate-spin" />A abrir o chat...</>
          ) : (
            <>Avançar<ArrowRight size={14} /></>
          )}
        </button>
      </div>
    </div>
  )
}

// ── helpers ────────────────────────────────────────────────────────────────

function backfillProgress(status: OnboardingStatus | null): number {
  if (!status) return 5
  const { backfills, progress_hint } = status
  const total = 3  // email, calendar, contacts (contacts pode falhar; ok)
  const completed =
    progress_hint.backfills_completed + progress_hint.backfills_failed
  // Interpola entre 10% (acabou de começar) e 90% (todos terminais)
  const base = 10 + (completed / total) * 80
  // Para cada backfill running, adiciona contribution proporcional a events
  let runningBoost = 0
  for (const state of Object.values(backfills)) {
    if (!state || state.status !== 'running') continue
    // Heurística simples: 500 events ≈ meio caminho para esse backfill
    const boost = Math.min(1, (state.events_seen || 0) / 500) * (80 / total / 2)
    runningBoost += boost
  }
  return Math.min(95, base + runningBoost)
}
